/**
 * useDesignerVariantSelection — Shared variant selection handler for designer nodes
 *
 * Updates picked_index, convenience shortcut field, and cacheKey when
 * the user picks a different variant from the history.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import { useCallback } from 'react'
import { useReactFlow } from 'reactflow'
import { useCanvasOptional } from '../../CanvasContext'
import type { HistoryEntry } from './types'

interface UseDesignerVariantSelectionOptions {
  nodeId: string
  historyEntries: HistoryEntry[]
  /** The data field name for the structured output (e.g., 'out_character_sheet') */
  outputFieldName: string
}

/**
 * Returns a handleHistorySelect callback that:
 * 1. Reads local_path from the selected history entry
 * 2. Updates the output object's picked_index
 * 3. Sets a new cacheKey (Date.now()) for cache busting
 * 4. Syncs to both React Flow state and canvas persistence
 */
export function useDesignerVariantSelection({
  nodeId,
  historyEntries,
  outputFieldName,
}: UseDesignerVariantSelectionOptions): (index: number) => void {
  const { setNodes, getNodes } = useReactFlow()
  const canvas = useCanvasOptional()

  return useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === nodeId)
      const currentOutput = currentNode?.data?.[outputFieldName]
      const newCacheKey = String(Date.now())
      const updatedOutput = {
        ...currentOutput,
        images: {
          ...currentOutput?.images,
          main: {
            ...currentOutput?.images?.main,
            picked_index: index,
          },
        },
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== nodeId
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  [outputFieldName]: updatedOutput,
                  out_image_path: localPath,
                  cacheKey: newCacheKey,
                },
              }
        )
      )
      canvas?.updateNodeData(nodeId, {
        [outputFieldName]: updatedOutput,
        out_image_path: localPath,
        cacheKey: newCacheKey,
      })
    },
    [nodeId, historyEntries, outputFieldName, getNodes, setNodes, canvas]
  )
}
